import React, {useEffect} from 'react';
import { Platform} from 'react-native';
import {DefaultTheme, NavigationContainer} from '@react-navigation/native';
import {SafeAreaProvider} from 'react-native-safe-area-context';
import RNBootSplash from 'react-native-bootsplash';

import {ReactQueryProvider} from './app/server/react-query';

import FastToastProvider from './app/components/toast/FastToast';
import ThemeRNE from './theme/theme-rne';
import {MenuProvider} from 'react-native-popup-menu';

import RootStackNavigator from './app/navigations';
import PwaInstallPrompt from './app/components/pwa/PwaInstallPrompt';
import {NavigationWebLinkingConfig} from './app/navigations/config';

import useStores, {StoreProvider} from './app/stores/useStores';
// import PushNotificationIOS from '@react-native-community/push-notification-ios';

const navTheme = DefaultTheme;
navTheme.colors.background = 'white';
navTheme.dark = false;

const Initializer = () => {
  const { authStore } = useStores();

  useEffect(() => {
    authStore.hydrate();
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || !('serviceWorker' in navigator)) {
      return;
    }

    // Register at startup so the browser can identify the site as an
    // installable web app before notification permission is requested.
    navigator.serviceWorker
      .register('/firebase-messaging-sw.js', {scope: '/'})
      .catch(error => {
        // PWA setup must never prevent the portal from rendering.
        console.warn('Unable to register the HouseCall web app:', error);
      });
  }, []);

  return null; // Just for hydration
};

const App = () => {
  const ringingNotif = require('./assets/audio/ringing.mp3');

  useEffect(() => {
    // Firebase web messaging is unavailable on some iOS/Safari versions and in
    // non-secure/private contexts. Notifications are optional and must not stop
    // the application (including the login page) from rendering.
    let unsubscribeOnMessage: (() => void) | undefined;

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data === 'Start ringing notification') {
        soundNotification(true);
      } else if (event.data === 'Stop ringing notification') {
        soundNotification(false);
      }
    };

    const handleForegroundNotification = (event: Event) => {
      const payload = (event as CustomEvent).detail;
      if (payload?.data?.title === 'INCOMING CALL') {
        soundNotification(true);
        alert(`${payload.data?.title} ${payload.data?.body}`); // eslint-disable-line no-alert
      } else {
        soundNotification(false);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener(
        'message',
        handleServiceWorkerMessage,
      );
    }

    window.addEventListener(
      'housecall-notification',
      handleForegroundNotification,
    );

    // Keep Firebase Messaging out of the critical login bundle. Older iOS
    // WebKit versions can load the portal even when web push is unsupported.
    import('./app/utils/web-notification-listener.utils')
      .then(module => module.startWebNotificationListener())
      .then(unsubscribe => {
        unsubscribeOnMessage = unsubscribe;
      })
      .catch(error => {
        // A messaging failure must not take down the rest of the web portal.
        console.warn('Web notifications are unavailable:', error);
      });

    return () => {
      unsubscribeOnMessage?.();
      window.removeEventListener(
        'housecall-notification',
        handleForegroundNotification,
      );
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener(
          'message',
          handleServiceWorkerMessage,
        );
      }
    };
  }, []);

  const onNavigationReady = () => {
    if (Platform.OS !== 'web') {
      setTimeout(() => {
        // RNBootSplash.hide({fade: true});
      }, 350);
    }
  };

  const soundNotification = (play: boolean) => {
    const audio = document.getElementById(
      'ringingNotification',
    ) as HTMLAudioElement;

    if (play) {
      audio.muted = false;
      audio.play();
    } else {
      audio.muted = true;
      audio.pause();
    }
  };

  return (
    <StoreProvider>
      <Initializer />
      <ReactQueryProvider>
        <ThemeRNE>
          <MenuProvider>
            <SafeAreaProvider>
              <NavigationContainer
                linking={{
                  enabled: true,
                  ...NavigationWebLinkingConfig,
                }}
                theme={navTheme}
                onReady={onNavigationReady}>
                <audio
                  loop
                  muted={true}
                  preload="auto"
                  id="ringingNotification"
                  src={ringingNotif.default}></audio>
                <FastToastProvider>
                  <RootStackNavigator />
                  <PwaInstallPrompt />
                </FastToastProvider>
              </NavigationContainer>
            </SafeAreaProvider>
          </MenuProvider>
        </ThemeRNE>
      </ReactQueryProvider>
    </StoreProvider>
  );
};

export default App;
